'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { trackCallClick, trackCTAClick, trackCallbackConversion } from '@/lib/analytics';

interface CommunicationOption {
  id: string;
  label: string;
  icon: string;
  href: string;
  bgColor: string;
  hoverColor: string;
}

interface FloatingCommunicationHubProps {
  phoneNumber?: string;
  whatsappNumber?: string;
  viberNumber?: string;
}

const FloatingCommunicationHub = ({
  phoneNumber = '0693499499',
  whatsappNumber = '0693499499',
  viberNumber = '0693499499',
}: FloatingCommunicationHubProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const communicationOptions: CommunicationOption[] = [
    {
      id: 'phone',
      label: 'Pozovi',
      icon: 'PhoneIcon',
      href: `tel:${phoneNumber}`,
      bgColor: 'bg-primary',
      hoverColor: 'hover:bg-primary/90',
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: 'ChatBubbleLeftRightIcon',
      href: `https://wa.me/381${whatsappNumber.substring(1)}`,
      bgColor: 'bg-success',
      hoverColor: 'hover:bg-success/90',
    },
    {
      id: 'viber',
      label: 'Viber',
      icon: 'ChatBubbleOvalLeftIcon',
      href: `viber://chat?number=381${viberNumber.substring(1)}`,
      bgColor: 'bg-secondary',
      hoverColor: 'hover:bg-secondary/90',
    },
    {
      id: 'sms',
      label: 'SMS',
      icon: 'EnvelopeIcon',
      href: `sms:${phoneNumber}`,
      bgColor: 'bg-warning',
      hoverColor: 'hover:bg-warning/90',
    },
  ];

  const handleCallClick = () => {
    trackCallClick(phoneNumber);
    trackCallbackConversion({
      type: 'phone',
      phoneNumber,
      source: 'floating_communication_hub',
    });
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleWhatsAppClick = () => {
    trackCTAClick('whatsapp', 'floating_communication_hub');
    trackCallbackConversion({
      type: 'whatsapp',
      phoneNumber: whatsappNumber,
      source: 'floating_communication_hub',
    });
    window.open(`https://wa.me/381${whatsappNumber.substring(1)}`, '_blank');
  };

  const handleViberClick = () => {
    trackCTAClick('viber', 'floating_communication_hub');
    trackCallbackConversion({
      type: 'viber',
      phoneNumber: viberNumber,
      source: 'floating_communication_hub',
    });
    window.open(`viber://chat?number=381${viberNumber.substring(1)}`, '_blank');
  };

  const handleOptionClick = (option: CommunicationOption) => {
    // Track conversion based on communication type
    const conversionType = option.id as 'phone' | 'whatsapp' | 'viber' | 'sms';
    
    trackCallbackConversion({
      type: conversionType,
      phoneNumber: phoneNumber,
      source: 'floating_communication_hub',
    });

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'communication_click', {
        event_category: 'conversion',
        event_label: `floating_hub_${option.id}`,
        value: option.id,
      });
    }
    setIsExpanded(false);
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'floating_hub_toggle', {
        event_category: 'engagement',
        event_label: isExpanded ? 'close' : 'open',
      });
    }
  };

  return (
    <div data-component-id="src/components/common/FloatingCommunicationHub.tsx:125:4" data-component-path="src/components/common/FloatingCommunicationHub.tsx" data-component-line="125" data-component-end-line="169" data-component-file="FloatingCommunicationHub.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20bottom-6%20right-6%20z-40%20flex%20flex-col-reverse%20items-end%20space-y-reverse%20space-y-3%22%7D" className="fixed bottom-6 right-6 z-40 flex flex-col-reverse items-end space-y-reverse space-y-3">
      {isExpanded && (
        <div data-component-id="src/components/common/FloatingCommunicationHub.tsx:127:8" data-component-path="src/components/common/FloatingCommunicationHub.tsx" data-component-line="127" data-component-end-line="147" data-component-file="FloatingCommunicationHub.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col-reverse%20space-y-reverse%20space-y-3%20mb-3%22%7D" className="flex flex-col-reverse space-y-reverse space-y-3 mb-3">
          {communicationOptions.map((option, index) => (
            <a data-component-id="src/components/common/FloatingCommunicationHub.tsx:129:12" data-component-path="src/components/common/FloatingCommunicationHub.tsx" data-component-line="129" data-component-end-line="145" data-component-file="FloatingCommunicationHub.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%7D"
              key={option.id}
              href={option.href}
              onClick={() => handleOptionClick(option)}
              className={`flex items-center space-x-3 ${option.bgColor} ${option.hoverColor} text-white px-4 py-3 rounded-full shadow-conversion hover:shadow-lg transition-all duration-250 transform hover:scale-105 animate-in slide-in-from-bottom-2`}
              style={{
                animationDelay: `${index * 50}ms`,
                animationFillMode: 'backwards',
              }}
              target={option.id === 'whatsapp' ? '_blank' : undefined}
              rel={option.id === 'whatsapp' ? 'noopener noreferrer' : undefined}
            >
              <Icon data-component-id="src/components/common/FloatingCommunicationHub.tsx:141:14" data-component-path="src/components/common/FloatingCommunicationHub.tsx" data-component-line="141" data-component-end-line="141" data-component-file="FloatingCommunicationHub.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={option.icon as any} size={20} />
              <span data-component-id="src/components/common/FloatingCommunicationHub.tsx:142:14" data-component-path="src/components/common/FloatingCommunicationHub.tsx" data-component-line="142" data-component-end-line="144" data-component-file="FloatingCommunicationHub.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-medium%20text-sm%20whitespace-nowrap%22%7D" className="font-medium text-sm whitespace-nowrap">
                {option.label}
              </span>
            </a>
          ))}
        </div>
      )}

      <button data-component-id="src/components/common/FloatingCommunicationHub.tsx:150:6" data-component-path="src/components/common/FloatingCommunicationHub.tsx" data-component-line="150" data-component-end-line="168" data-component-file="FloatingCommunicationHub.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
        onClick={handleToggle}
        className={`flex items-center justify-center w-14 h-14 ${
          isExpanded ? 'bg-destructive' : 'bg-primary'
        } text-white rounded-full shadow-conversion hover:shadow-lg transition-all duration-250 hover:scale-110 group relative overflow-hidden`}
        aria-label={isExpanded ? 'Zatvori meni' : 'Otvori meni'}
      >
        <span data-component-id="src/components/common/FloatingCommunicationHub.tsx:157:8" data-component-path="src/components/common/FloatingCommunicationHub.tsx" data-component-line="157" data-component-end-line="157" data-component-file="FloatingCommunicationHub.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20inset-0%20bg-white%2F20%20transform%20scale-0%20group-hover%3Ascale-100%20transition-transform%20duration-250%20rounded-full%22%7D" className="absolute inset-0 bg-white/20 transform scale-0 group-hover:scale-100 transition-transform duration-250 rounded-full"></span>
        <Icon data-component-id="src/components/common/FloatingCommunicationHub.tsx:158:8" data-component-path="src/components/common/FloatingCommunicationHub.tsx" data-component-line="158" data-component-end-line="164" data-component-file="FloatingCommunicationHub.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D"
          name={isExpanded ? 'XMarkIcon' : 'ChatBubbleLeftEllipsisIcon'}
          size={28}
          className={`relative z-10 transition-transform duration-250 ${
            isExpanded ? 'rotate-90' : 'rotate-0'
          }`}
        />
        {!isExpanded && (
          <span data-component-id="src/components/common/FloatingCommunicationHub.tsx:166:10" data-component-path="src/components/common/FloatingCommunicationHub.tsx" data-component-line="166" data-component-end-line="166" data-component-file="FloatingCommunicationHub.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20-top-1%20-right-1%20w-4%20h-4%20bg-accent%20rounded-full%20animate-pulse%22%7D" className="absolute -top-1 -right-1 w-4 h-4 bg-accent rounded-full animate-pulse"></span>
        )}
      </button>
    </div>
  );
};

export default FloatingCommunicationHub;